import { useCallback, useEffect, useRef, useState } from 'react';
import cardBack from '../assets/games/bloodmoon-card-back.webp';
import cardFront from '../assets/cards/card-front.png';
import roleBar from '../assets/cards/role-bar.png';

/**
 * HoloRoleCard — the polished role-reveal card.
 *
 * Movement / glare / flip are lifted from simeydotme/pokemon-cards-css:
 *  - a perspective scene wraps a `.card` that rotates toward the pointer
 *    (rotateX/rotateY driven by CSS vars set on every pointer move);
 *  - a `.glare` layer is a radial highlight that tracks the pointer with
 *    `mix-blend-mode: overlay`, fading in with pointer distance from centre;
 *  - the reveal is a 3D Y-flip between the ornate back and the composed front.
 *
 * Hold-to-reveal is preserved: press and hold the back for `holdMs` (a progress
 * ring fills) and the card flips to the role face. Release before then cancels.
 *
 * Self-contained — used by the card lab today; drops into RoleRevealScreen later.
 */

export interface RoleCardDef {
  /** Role display name (e.g. "Seer"). */
  name: string;
  /** Imported character art (transparent PNG). */
  character: string;
  /** One-line description under the name. */
  description: string;
  /** CSS gradient for the name text (role accent). */
  nameGradient: string;
  /** Show the blood moon behind the character (killers only). Default false. */
  showMoon?: boolean;
  /** Blood-moon disc colour (only used when `showMoon`). */
  moonColor?: string;
  /** Ambient accent glow for the art window + card edge. */
  glowColor: string;
  /** Fine-tune the character's vertical placement in the window (CSS length). */
  charBottom?: string;
  /** Fine-tune the character scale in the window (0–1+). */
  charScale?: number;
}

export interface HoloRoleCardProps {
  def: RoleCardDef;
  /**
   * Ornate card-back art (shown before reveal). Defaults to the Blood Moon
   * back so existing callers render byte-identical. Pass a game-specific back
   * (e.g. the Whisper's arcana tapestry) via this prop instead of forking the
   * component.
   */
  backSrc?: string;
  /** Card width (any CSS length). Height derives from the 876×1572 art ratio. */
  width?: string;
  /** Hold duration before the flip fires (ms). Default 700. */
  holdMs?: number;
  /** Start already revealed (skips the hold — handy for the lab). */
  startRevealed?: boolean;
  /**
   * Controlled reveal. When provided, the PARENT owns reveal state (the game
   * needs this for its sticky-reveal / hold-to-peek states); the card renders
   * this value and delegates the reveal decision to the callbacks below.
   */
  revealed?: boolean;
  /** Fired when a hold completes. In controlled mode the parent acts on it. */
  onReveal?: () => void;
  /** Fired on press (hold start). */
  onHoldStart?: () => void;
  /** Fired on release / pointer-leave (the game hides a peek here). */
  onHoldEnd?: () => void;
}

const CARD_ASPECT = 876 / 1572; // ≈ 0.557
const MAX_TILT = 16; // deg
const HOLD_TICK = 25; // ms

export function HoloRoleCard({
  def,
  backSrc,
  width = 'min(86vw, 340px)',
  holdMs = 700,
  startRevealed = false,
  revealed: revealedProp,
  onReveal,
  onHoldStart,
  onHoldEnd,
}: HoloRoleCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const isControlled = revealedProp !== undefined;
  const [internalRevealed, setInternalRevealed] = useState(startRevealed);
  const shown = isControlled ? (revealedProp as boolean) : internalRevealed;
  const [interacting, setInteracting] = useState(false);
  const [holdProgress, setHoldProgress] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  const holdTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const holdStart = useRef(0);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(mq.matches);
    const h = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);

  // ── Pointer-driven tilt + glare (writes CSS vars straight to the node, so a
  //    60fps pointer stream never triggers a React re-render) ─────────────────
  const applyPointer = useCallback(
    (clientX: number, clientY: number) => {
      const el = cardRef.current;
      if (!el || reducedMotion) return;
      const r = el.getBoundingClientRect();
      const px = (clientX - r.left) / r.width; // 0..1
      const py = (clientY - r.top) / r.height; // 0..1
      const rotY = (px - 0.5) * 2 * MAX_TILT; // pointer right → tilt right
      const rotX = -(py - 0.5) * 2 * MAX_TILT; // pointer down → tilt away
      const fromCenter = Math.min(1, Math.hypot(px - 0.5, py - 0.5) * 2);
      el.style.setProperty('--rx', `${rotX.toFixed(2)}deg`);
      el.style.setProperty('--ry', `${rotY.toFixed(2)}deg`);
      el.style.setProperty('--mx', `${(px * 100).toFixed(1)}%`);
      el.style.setProperty('--my', `${(py * 100).toFixed(1)}%`);
      el.style.setProperty('--glare', `${(0.35 + fromCenter * 0.5).toFixed(2)}`);
    },
    [reducedMotion],
  );

  const resetPointer = useCallback(() => {
    const el = cardRef.current;
    if (!el) return;
    el.style.setProperty('--rx', '0deg');
    el.style.setProperty('--ry', '0deg');
    el.style.setProperty('--mx', '50%');
    el.style.setProperty('--my', '50%');
    el.style.setProperty('--glare', '0');
  }, []);

  // ── Hold-to-reveal ──────────────────────────────────────────────────────────
  const stopHold = useCallback(() => {
    if (holdTimer.current) {
      clearInterval(holdTimer.current);
      holdTimer.current = null;
    }
    setHoldProgress(0);
  }, []);

  const startHold = useCallback(() => {
    if (shown || holdTimer.current) return;
    onHoldStart?.();
    holdStart.current = Date.now();
    holdTimer.current = setInterval(() => {
      const p = Math.min(1, (Date.now() - holdStart.current) / holdMs);
      setHoldProgress(p);
      if (p >= 1) {
        stopHold();
        if (isControlled) onReveal?.();
        else setInternalRevealed(true);
      }
    }, HOLD_TICK);
  }, [shown, holdMs, stopHold, isControlled, onReveal, onHoldStart]);

  useEffect(() => () => stopHold(), [stopHold]);

  // ── Pointer handlers ─────────────────────────────────────────────────────────
  const onPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    setInteracting(true);
    applyPointer(e.clientX, e.clientY);
    startHold();
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!interacting && e.pointerType === 'touch') return; // touch: only while down
    setInteracting(true);
    applyPointer(e.clientX, e.clientY);
  };
  const endInteract = () => {
    setInteracting(false);
    resetPointer();
    stopHold();
    onHoldEnd?.();
  };

  const holdHintVisible = !shown;

  return (
    <div className="hrc-scene" style={{ width, aspectRatio: `${CARD_ASPECT}` }}>
      <style>{CARD_CSS}</style>
      <div
        ref={cardRef}
        className={`hrc-card${interacting ? ' is-interacting' : ''}${shown ? ' is-revealed' : ''}`}
        style={{ ['--glow' as string]: def.glowColor }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endInteract}
        onPointerLeave={endInteract}
        onPointerCancel={endInteract}
        onContextMenu={(e) => e.preventDefault()}
        role="button"
        aria-label={shown ? `${def.name} — your role` : 'Press and hold to reveal your role'}
        tabIndex={0}
      >
        <div className={`hrc-flipper${shown ? ' is-flipped' : ''}`}>
          {/* ── BACK (kept as-is: the ornate card-back art) ── */}
          <div className="hrc-face hrc-face--back">
            <img className="hrc-art" src={backSrc ?? cardBack} alt="" draggable={false} />
            <div className="hrc-glare" />
            {holdHintVisible && (
              <div className="hrc-hint">
                <div className="hrc-hint-ring" aria-hidden>
                  <svg viewBox="0 0 72 72" width="72" height="72">
                    <circle className="hrc-ring-track" cx="36" cy="36" r="32" />
                    <circle
                      className="hrc-ring-fill"
                      cx="36"
                      cy="36"
                      r="32"
                      style={{ strokeDashoffset: 201.06 * (1 - holdProgress) }}
                    />
                  </svg>
                  <span className="hrc-hint-icon">✦</span>
                </div>
                <span className="hrc-hint-label">
                  {holdProgress > 0 ? 'Hold…' : 'Hold to reveal'}
                </span>
              </div>
            )}
          </div>

          {/* ── FRONT (composed role card) ── */}
          <div className="hrc-face hrc-face--front">
            <img className="hrc-frame" src={cardFront} alt="" draggable={false} />
            <div className="hrc-inner">
              <div
                className={`hrc-window${def.showMoon ? '' : ' no-moon'}`}
                style={{ ['--moon' as string]: def.moonColor ?? def.glowColor }}
              >
                {def.showMoon && <div className="hrc-moon" />}
                {def.showMoon && <div className="hrc-zodiac" />}
                <img
                  className="hrc-char"
                  src={def.character}
                  alt={def.name}
                  draggable={false}
                  style={{
                    bottom: def.charBottom ?? '0%',
                    transform: `translateX(-50%) scale(${def.charScale ?? 1})`,
                  }}
                />
                <div className="hrc-window-frame" />
              </div>
              <img className="hrc-rolebar" src={roleBar} alt="Role" draggable={false} />
              <div className="hrc-textpanel">
                <div className="hrc-name" style={{ backgroundImage: def.nameGradient }}>
                  {def.name}
                </div>
                <div className="hrc-desc">{def.description}</div>
              </div>
            </div>
            <div className="hrc-glare" />
          </div>
        </div>
      </div>
    </div>
  );
}

const CARD_CSS = `
.hrc-scene {
  perspective: 1400px;
  display: inline-block;
  --rx: 0deg; --ry: 0deg; --mx: 50%; --my: 50%; --glare: 0;
}
.hrc-card {
  position: relative;
  width: 100%; height: 100%;
  transform-style: preserve-3d;
  transform: rotateX(var(--rx)) rotateY(var(--ry));
  transition: transform 0.8s cubic-bezier(0.23, 1, 0.32, 1);
  cursor: pointer;
  user-select: none; -webkit-user-select: none; -webkit-touch-callout: none;
  touch-action: none;
  will-change: transform;
}
.hrc-card.is-interacting { transition: transform 0.06s linear; }
.hrc-card.is-revealed {
  filter: drop-shadow(0 18px 40px rgba(0,0,0,0.6))
          drop-shadow(0 0 26px color-mix(in srgb, var(--glow) 45%, transparent));
}
.hrc-card:not(.is-revealed) {
  filter: drop-shadow(0 14px 34px rgba(0,0,0,0.6));
}

.hrc-flipper {
  position: relative;
  width: 100%; height: 100%;
  transform-style: preserve-3d;
  transition: transform 0.75s cubic-bezier(0.6, 0.05, 0.1, 1);
}
.hrc-flipper.is-flipped { transform: rotateY(180deg); }

.hrc-face {
  position: absolute; inset: 0;
  backface-visibility: hidden; -webkit-backface-visibility: hidden;
  border-radius: 18px; overflow: hidden;
  transform-style: preserve-3d;
}
.hrc-face--front { transform: rotateY(180deg); background: #150a0b; }

.hrc-art, .hrc-frame {
  position: absolute; inset: 0;
  width: 100%; height: 100%; object-fit: cover;
  pointer-events: none; user-select: none;
}

/* ── Glare (pointer-tracked highlight) ── */
.hrc-glare {
  position: absolute; inset: 0;
  border-radius: inherit;
  background: radial-gradient(
    farthest-corner circle at var(--mx) var(--my),
    rgba(255,255,255,0.55) 5%,
    rgba(255,255,255,0.18) 22%,
    rgba(0,0,0,0.35) 90%
  );
  mix-blend-mode: overlay;
  opacity: var(--glare);
  transition: opacity 0.4s ease;
  pointer-events: none;
}
.hrc-card.is-interacting .hrc-glare { transition: opacity 0.06s linear; }

/* ── FRONT composition ── */
.hrc-inner {
  position: absolute;
  top: 4.2%; bottom: 4.2%; left: 6.6%; right: 6.6%;
  display: flex; flex-direction: column;
  pointer-events: none;
}
.hrc-window {
  position: relative;
  height: 54%;
  border-radius: 10px;
  overflow: hidden;
  background:
    radial-gradient(120% 90% at 50% 8%, color-mix(in srgb, var(--moon) 70%, #1a0406) 0%, transparent 55%),
    radial-gradient(140% 120% at 50% 120%, color-mix(in srgb, var(--moon) 30%, #0a0305) 0%, #070204 60%);
}
/* Positive roles (seer / villager): no blood moon — a soft accent glow instead. */
.hrc-window.no-moon {
  background:
    radial-gradient(120% 82% at 50% 14%, color-mix(in srgb, var(--glow) 42%, #150a16) 0%, transparent 62%),
    radial-gradient(150% 130% at 50% 118%, color-mix(in srgb, var(--glow) 16%, #0a0608) 0%, #070406 64%);
}
.hrc-moon {
  position: absolute; left: 50%; top: 6%;
  width: 62%; aspect-ratio: 1; transform: translateX(-50%);
  border-radius: 50%;
  background: radial-gradient(circle at 50% 45%,
    color-mix(in srgb, var(--moon) 85%, #fff 15%) 0%,
    var(--moon) 45%,
    color-mix(in srgb, var(--moon) 60%, #200) 72%,
    transparent 78%);
  filter: blur(0.5px);
  opacity: 0.9;
  animation: hrc-breathe 5s ease-in-out infinite;
}
.hrc-zodiac {
  position: absolute; inset: 0;
  background: radial-gradient(circle at 50% 40%, transparent 38%, rgba(255,230,180,0.05) 39%, transparent 41%),
              radial-gradient(circle at 50% 40%, transparent 46%, rgba(255,230,180,0.04) 47%, transparent 49%);
  opacity: 0.6;
}
.hrc-char {
  position: absolute; left: 50%;
  width: 100%; height: 108%;
  object-fit: contain; object-position: bottom;
  filter: drop-shadow(0 6px 14px rgba(0,0,0,0.55));
  pointer-events: none; user-select: none;
}
.hrc-window-frame {
  position: absolute; inset: 0;
  border-radius: 10px;
  border: 2px solid transparent;
  background: linear-gradient(160deg, #d8a24e, #7a4c1e 30%, #3a2410 55%, #c9923f 100%) border-box;
  -webkit-mask: linear-gradient(#000 0 0) padding-box, linear-gradient(#000 0 0);
  -webkit-mask-composite: xor; mask-composite: exclude;
  box-shadow: inset 0 0 18px rgba(0,0,0,0.7);
}

.hrc-rolebar {
  width: 100%; height: auto;
  margin-top: -1.5%;
  object-fit: contain;
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5));
}

.hrc-textpanel {
  flex: 1;
  display: flex; flex-direction: column; align-items: center;
  justify-content: flex-start;
  text-align: center;
  padding: 7% 4% 0;
}
.hrc-name {
  font-family: var(--font-display, 'Grenze', serif);
  font-size: clamp(26px, 8.5cqw, 40px);
  line-height: 1;
  margin-bottom: 5%;
  -webkit-background-clip: text; background-clip: text;
  -webkit-text-fill-color: transparent; color: transparent;
  filter: drop-shadow(0 2px 6px rgba(0,0,0,0.5));
}
.hrc-desc {
  font-family: var(--font-ui, Inter, system-ui, sans-serif);
  font-size: clamp(12px, 4cqw, 16px);
  line-height: 1.45;
  color: rgba(224, 205, 205, 0.82);
  max-width: 90%;
}
.hrc-scene { container-type: inline-size; }

/* ── Hold hint on the back ── */
.hrc-hint {
  position: absolute; inset: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 10px;
  transform: translateY(12px);
  pointer-events: none;
}
/* Soft dark backdrop so the ring + text read over the busy card back. */
.hrc-hint::before {
  content: '';
  position: absolute;
  width: 62%; height: 42%;
  border-radius: 50%;
  background: radial-gradient(ellipse at center,
    rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.42) 42%, transparent 72%);
  filter: blur(10px);
}
.hrc-hint-ring { position: relative; z-index: 1; width: 72px; height: 72px; }
.hrc-hint-ring svg { transform: rotate(-90deg); filter: drop-shadow(0 2px 6px rgba(0,0,0,0.85)); }
.hrc-ring-track { fill: none; stroke: rgba(255,255,255,0.14); stroke-width: 4; }
.hrc-ring-fill {
  fill: none; stroke: #e6b866; stroke-width: 4; stroke-linecap: round;
  stroke-dasharray: 201.06;
  transition: stroke-dashoffset 0.05s linear;
  filter: drop-shadow(0 0 5px rgba(230,184,102,0.8));
}
.hrc-hint-icon {
  position: absolute; inset: 0; display: grid; place-items: center;
  color: #e6b866; font-size: 22px; text-shadow: 0 0 8px rgba(230,184,102,0.7);
  animation: hrc-pulse 2s ease-in-out infinite;
}
.hrc-hint-label {
  position: relative; z-index: 1;
  font-family: var(--font-ui, Inter, system-ui, sans-serif);
  font-size: 13px; letter-spacing: 0.06em; font-weight: 600;
  color: #ffffff;
  text-shadow: 0 2px 6px rgba(0,0,0,0.95), 0 0 4px rgba(0,0,0,0.85);
}

@keyframes hrc-breathe { 0%,100% { opacity: 0.78; } 50% { opacity: 1; } }
@keyframes hrc-pulse { 0%,100% { transform: scale(1); opacity: 0.85; } 50% { transform: scale(1.12); opacity: 1; } }

@media (prefers-reduced-motion: reduce) {
  .hrc-card, .hrc-flipper, .hrc-glare { transition: none; }
  .hrc-moon, .hrc-hint-icon { animation: none; }
}
`;
